import { NextPage } from 'next';
import { MultiSliderSection } from 'components/structures/main/Slide';
import { ImageDescription } from 'components/structures/general';
import {
  HandShakeIcon,
  ImprovementIcon,
  IndustriesAirIcon,
  IndustriesLampIcon,
  IndustriesPipeIcon,
  IndustriesTvIcon,
  ListIcon,
  TeamIcon,
  TruckIcon,
} from 'svgs';
import { IndustriesSlider, VideoBlock } from 'templates/company';
import {
  companyMission,
  companyMission2x,
  uSSSAgileWhite,
  uSSSAgileDark,
  uSSSGlance,
  uSSSGlance2x,
  companyMissionBg,
  companyOverview,
  companyOverview2x,
  companyHistory,
  companyHistory2x,
  companyServices,
  companyServices2x,
} from 'images/company';
import { useDarkMode } from 'hooks';
import { ParallaxGallery } from 'templates/company';
import { careersBgSvg } from 'images/services';
import React from 'react';
import { SEO } from 'templates/layout';

const Company: NextPage = () => {
  const { isDarkMode } = useDarkMode();

  return (
    <>
      <SEO
        title="Contact Us"
        description="Learn more about our software solutions company, our web development services, mission, values, and the IT services we’re providing."
      />
      <VideoBlock button={'Contact Us'} link={'/contact-us'} title={'Innovative Software Development'} text={'Watch video'} />
      <MultiSliderSection
        subTitle="Our standards"
        title="Core Values"
        text="Our core values guide and inspire us to provide high-quality software solutions to our customers."
        sliderList={[
          {
            svgIcon: <ListIcon width="100%" viewBox="0 0 39 39" />,
            title: 'Deliver Results',
            text: 'US Software is an innovation and engineering company that advises clients on product strategy, engineering, design, and culture, then partners with them to build and launch technology-driven solutions to their problems.',
          },
          {
            svgIcon: <HandShakeIcon width="100%" viewBox="0 0 39 39" />,
            title: 'Ownership',
            text: 'Unlike the traditional players, we optimize for small teams, lean budgets, consistent and sustainable velocity, transparency, and expertise. We build a long-lasting relationship, not just deliverables.',
          },
          {
            svgIcon: <ImprovementIcon width="100%" viewBox="0 0 39 39" />,
            title: 'Frugality',
            text: 'Accomplishing more with less. Constraints breed resourcefulness, self-sufficiency, and invention. There are no extra points for growing headcount, budget size, or fixed expense.',
          },
          {
            svgIcon: <TeamIcon width="100%" viewBox="0 0 39 39" />,
            title: 'Customer-Centered',
            text: 'We opt for a user-driven experience that is custom-tailored to meet any needs. We know that putting the customer above all else is nothing new, but here’s the thing – we care about every project and put our everything to the table.',
          },
        ]}
      />
      <ImageDescription
        image={{ image: companyOverview, srcSet: `${companyOverview} 1x, ${companyOverview2x} 2x` }}
        upperTitle="Who we are"
        title="Company overview"
        text={
          'US Software Solutions is one of the fastest-growing IT companies on the market right now. Our software solutions enable our customers to significantly simplify access to services provided and make their new business solutions even more attractive to potential clients.'
        }
        advanceText={{
          text: "\n We go through each stage of product development hand in hand with the client to fully satisfy their needs for convenient and intuitive software solutions. Our philosophy is based on mutually beneficial interaction with customers, which is reflected in our products. Instead of thinking on an unattainable scale, we listen carefully to the client's preferences. It allows us to act pragmatically, with a focus on long-term cooperation. \n\n Our company is a cross-functional team of professionals with extensive software development experience in various fields. Our specialists are always ready to offer an effective solution, create a new product, remodel and improve a current project, or adjust the code to increase operational efficiencies.",
          buttonClose: 'Hide',
          buttonOpen: 'Read More',
        }}
      />
      <ImageDescription
        image={{ image: companyHistory, srcSet: `${companyHistory} 1x, ${companyHistory2x} 2x` }}
        upperTitle="Our story"
        title="Company history"
        align="right"
        text="US Software Solutions was established as a subsidiary of US Energy Solutions, America's premiere ESCO. The idea was simple - to gather under our wing a team of highly qualified IT specialists - developers, designers, business analysts, marketers, and project managers. Professionals capable of producing exciting and unique digital products."
        advanceText={{
          text: "\nInitially, USSS served US Energy Solutions by creating efficient digital products for Utilities, Energy industry entities, and Renewable Energy providers. However, since today's world is changing at a rapid pace, it is no longer enough to be a certified expert. It is crucial to optimize all internal and external company processes for the business to deliver convenient and intuitive solutions even to those unfamiliar with the Energy industry.\n\n We started small, with only five employees. At first, this was enough, but no matter how talented our specialists were, the question of expanding the staff soon arose. Thanks to our rapid and successful expansion, USSS's international team now consists of dozens of IT specialists. These are all like-minded, talented individuals fully equipped to serve Utilities, Renewable Energy vendors, Energy companies, and the Telecommunications industry.\n Today’s Energy industry landscape provides new opportunities for further innovation. We are delighted to see the implementation of new technologies and always keep up with the times, remaining true to our core principles.",
          buttonClose: 'Hide',
          buttonOpen: 'Read More',
        }}
      />
      <ParallaxGallery />
      <ImageDescription
        image={{ image: companyServices, srcSet: `${companyServices} 1x, ${companyServices2x} 2x` }}
        bgImage={{ image: careersBgSvg }}
        bgColor="#116666"
        onlyWhiteText
        fullImage
        showRedCircleIcon
        upperTitle="What we offer"
        title="Company services"
        text={
          "We provide reliable, efficient, cost-saving, and smart technologies for B2C and B2B interactions. Cutting-edge products and services are offered to make your transition to the future utility ecosystem more effortless and seamless. \n\nBy carefully analyzing our clients' needs and requirements for future software, we create projects that contribute to your business's success, development, and efficiency."
        }
        // link={{ href: '/services', text: 'Services' }}
      />
      <ImageDescription
        image={{ image: companyMission, srcSet: `${companyMission} 1x, ${companyMission2x} 2x`, imageCenterLg: true }}
        bgImage={{ image: companyMissionBg }}
        upperTitle="Our priorities"
        title="Company mission"
        align="right"
        text={
          "We are focused on providing out of the box systems built for energy brokers and sales agents to utilize technology and digitalize their process to make their business run more efficiently enabling them to be more profitable.\n\n Thanks to an individual approach to each client, USSS specialists can quickly build complicated software, from simple business applications to complex systems that best meet a particular customer's requirements."
        }
      />
      <ImageDescription
        image={{ image: isDarkMode ? uSSSAgileDark : uSSSAgileWhite, imageCenterLg: true }}
        upperTitle="Our workflow"
        title="USSS Agile in a nutshell"
        text={
          "At USSS, we firmly believe that people and interactions are more important than processes and tools.\n\n In Agile methodology, the priority is not the initial setting but the user's actual needs. It is usual for Agile to continually make changes to the technical specification, even amid development. \n\n Agile implies going through a series of cycles - iterations. Each iteration is a separate project, where a fragment of the program is developed, functionality is improved, and new capabilities are added."
        }
      />
      <ImageDescription
        image={{ image: uSSSGlance, srcSet: `${uSSSGlance} 1x, ${uSSSGlance2x} 2x,`, imageCenterLg: true }}
        upperTitle="Our know-how"
        title="USSS at a glance"
        align="right"
        text={
          'US Software Solutions is a software development company focused on providing digital technologies to all types of businesses globally.\n\n Over the past years, we have developed specialized software tools for our parent company US Energy Solutions, and a network of customers across numerous industries and sectors.'
        }
      />
      <IndustriesSlider
        sliderList={[
          {
            text: {
              title: 'Energy Efficiency',
              description:
                "We understand how important it is in today's realities to be an energy-efficient business. Our software solutions aim at facilitating the interaction between energy suppliers and consumers.",
            },
            icon: <IndustriesLampIcon />,
            button: { label: 'LED4Free', linkTo: '/led4free' },
          },
          {
            text: {
              title: 'Renewable Energy',
              description:
                'RE is the future and its diversified production is on continuous rise. Our goal is the competent and gradual promotion of RE sources via innovative software solutions. ',
            },
            icon: <IndustriesAirIcon />,
            button: { label: 'US Energy Solutions', linkTo: '/us-energy-solutions' },
          },
          {
            text: {
              title: 'Utilities',
              description:
                'Software integration for Utilities is an integral part of our workflow. Modern software allows Utilities to gain full control over their provided solutions and consumers to access better-quality services.',
            },
            icon: <IndustriesPipeIcon />,
            button: { label: 'Retail Energy', linkTo: '/retail-energy' },
          },
          {
            text: {
              title: 'Telecommunications',
              description:
                'Telecom software allows you to keep up with the ever-growing telecommunications demands facilitating flexible technologies that support various features and ensure smooth operations for telecom providers.,',
            },
            icon: <IndustriesTvIcon />,
            button: { label: 'Utilities One', linkTo: '/utilities-one' },
          },
          {
            text: {
              title: 'Logistics',
              description:
                "We realize the ever-growing need for automating logistics processes. That's precisely why we implement cutting-edge software solutions to speed up freight forwarding effectively.",
            },
            icon: <TruckIcon />,
            button: { label: 'Patron Trucking', linkTo: '/patron-trucking' },
          },
        ]}
      />
    </>
  );
};

export default Company;
